import { Component, EventEmitter, Inject, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { DocumentEditorType, DocumentInfo, DocumentPermission, IoniconsFilled, LockableResources } from '@pt/pt-models';
import { ResourceLockManager } from '@pt/api-client';
import { takeUntil } from 'rxjs/operators';
import { DocResourceStatus } from 'it/primatoo/openapi/api/org/schemas/common/DocResourceStatus';
import { NgrxWidgetSelectors, WidgetEffects } from '../documents';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import {
  DocumentPermissionService,
  PtCoreSelectors,
  PtWidgetModuleConfig,
  WidgetConfig,
} from '@pt/pt-core';
import { PrimatooModule } from 'it/primatoo/openapi/api/org/schemas/common/PrimatooModule';
import { changeLock } from 'libs/pt-core/src/lib/store/actions/pt-core.actions';

@Component({
  selector: 'pt-document-lock',
  template: `
    <div class="locker" *ngIf="showLockIcon">
      <pt-icon
        *ngIf='lockAcquired'
        [key]='IconLock'
        class='text-success d-flex'
      ></pt-icon>

      <pt-icon
        *ngIf='!lockAcquired'
        [key]='IconUnlockLock'
        class='text-danger d-flex'
      ></pt-icon>
    </div>
  `,
  styles: [`
    .locker {
      position: fixed;
      z-index: 1000000;
      top: 58px;
      right: 125px;
      width: 30px;
      height: 30px;
      border-radius: 25px;
      background: white;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  `]
})
export class DocumentLockComponent implements OnInit, OnDestroy {
  @Input() showLockIcon = false;
  @Input() lockableResource: LockableResources;
  @Output() changeLock = new EventEmitter<boolean>();
  @Output() changeDocumentPermission = new EventEmitter<DocumentPermission>();
  @Output() noChapterPermission = new EventEmitter<boolean>();
  public lockAcquired = false;
  public IconUnlockLock = IoniconsFilled.lockOpen;
  public IconLock = IoniconsFilled.lock;
  private documentInfo$: Observable<DocumentInfo> = this.store.select(NgrxWidgetSelectors.getDocInfo);
  private activeChapter$: Observable<Chapter> = this.store.select(NgrxWidgetSelectors.getActiveChapterInfo);
  private activeModules$: Observable<PrimatooModule[]> = this.store.select(PtCoreSelectors.activeModules);
  private activeModules?: PrimatooModule[] = undefined;
  private activeChapter?: Chapter = undefined;


  private resourceLockManager: ResourceLockManager;
  private readonly lockDurationSec!: number;
  private documentInfo?: DocumentInfo;
  private documentPermission?: DocumentPermission;
  private destroyResourceLockManager$ = new Subject<boolean>();
  private destroy$ = new Subject<boolean>();

  constructor(
    @Inject(WidgetConfig) public config: PtWidgetModuleConfig,
    private store: Store,
    private documentPermissionService: DocumentPermissionService,
    private injector: Injector) {
    this.lockDurationSec = config.env.lockDurationSec;
  }


  ngOnDestroy(): void {
    this.releaseResourceLockManager();
    this.propagateChangeLock(false);
    this.destroyResourceLockManager$.next(true);
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.noChapterPermission.emit(false);

    this.activeModules$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((activeModules: PrimatooModule[]) => {
      this.activeModules = activeModules;
      this.alignChangeActiveChapterOrActiveModule(this.activeChapter, this.documentPermission, this.activeModules);
    });

    this.activeChapter$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((activeChapter: Chapter) => {
      this.activeChapter = activeChapter;
      this.alignChangeActiveChapterOrActiveModule(this.activeChapter, this.documentPermission, this.activeModules);
    });

    this.documentInfo$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((documentInfo: DocumentInfo) => {
      if (this.documentInfo?.docId || documentInfo?.docId) {
        this.documentInfo = documentInfo;
        if (this.documentInfo) {
          this.initLockManager(documentInfo.docType, documentInfo.docId, documentInfo.status);
        } else {
          this.releaseResourceLockManager();
        }
      }
    });
    this.propagateChangeLock(this.lockAcquired);
  }

  private initLockManager(docType: DocumentEditorType, docId: number, docStatus?: DocResourceStatus) {
    this.documentPermissionService.getDocumentPermission(docType, docId).subscribe((doc: DocumentPermission) => {
      this.documentPermission = doc;
      this.changeDocumentPermission.emit(this.documentPermission);
      const writeAccess = this.documentPermission?.writeAccess;
      if (!this.resourceLockManager) {
        this.releaseResourceLockManager();
        if (writeAccess) {
          switch (docStatus) {
            case DocResourceStatus.PUBLISHED:
              // tolgo il lock nel caso lo avevo
              this.propagateChangeLock(false);
              break;
            case DocResourceStatus.DRAFT:
              this.resourceLockManager = new ResourceLockManager(
                this.lockableResource,
                docId,
                this.lockDurationSec,
                this.injector);

              this.resourceLockManager.resourceLockAcquired.pipe(
                takeUntil(this.destroyResourceLockManager$)
              ).subscribe((lockAcquired: boolean) => {
                this.propagateChangeLock(lockAcquired);
              });
              break;
          }
        } else {
          // tolgo il lock nel caso lo avevo
          this.propagateChangeLock(false);
        }
      }
    }, () => {
      // tolgo il lock nel caso lo avevo
      this.propagateChangeLock(false);
    });
  }

  private releaseResourceLockManager() {
    if (this.resourceLockManager) {
      this.resourceLockManager.release();
      this.resourceLockManager = undefined;
    }
    this.destroyResourceLockManager$.next(true);
  }

  private propagateChangeLock(lockAcquired: boolean) {
    if (this.lockAcquired !== lockAcquired) {
      this.lockAcquired = lockAcquired;
      this.store.dispatch(changeLock({lock: this.lockAcquired }))
      this.changeLock.emit(this.lockAcquired);
    }
  }

  private alignChangeActiveChapterOrActiveModule(currentChapter: Chapter, documentPermission: DocumentPermission, activeModule: PrimatooModule[]) {
    if (currentChapter && documentPermission && activeModule) {
      const chapterEditable = WidgetEffects.editableChapter(documentPermission.operations, currentChapter.selector, activeModule);
      if (chapterEditable) {
        this.noChapterPermission.emit(false);
        this.initLockManager(this.documentInfo?.docType, this.documentInfo?.docId, this.documentInfo?.status);
      } else {
        this.noChapterPermission.emit(true);
        this.releaseResourceLockManager();
      }
    } else {
      this.releaseResourceLockManager();
    }
  }
}
